import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import {
  BusinessFilters,
  BusinessList,
  BusinessInfoModal,
} from "../../../ui/Business";
import { Icon, Button } from "../../../ui";
import { useBusinesses, useUrlStates } from "@finitless/shared/hooks";
import {
  BUSINESS_FILTER_TABS,
  BUSINESS_ITEMS_PER_PAGE,
} from "@finitless/shared/constants";

const StoresPage = ({ selectedBrand }) => {
  const { t } = useTranslation("stores");
  const navigate = useNavigate();

  // URL-persisted state (tab, search, page)
  const [urlStates, updateUrlState] = useUrlStates({
    filter: "enabled",
    searchTerm: "",
    currentPage: "1",
  });

  const [filter, setFilter] = useState(urlStates.filter || "enabled");
  const [searchInput, setSearchInput] = useState(urlStates.searchTerm || "");
  const [searchTerm, setSearchTerm] = useState(urlStates.searchTerm || "");
  const [currentPage, setCurrentPage] = useState(
    parseInt(urlStates.currentPage) || 1,
  );
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
  }, [searchTerm]);

  // Parallel count queries (pageSize: 1 — only need pagination.total)
  const { data: allData } = useBusinesses({
    enabledFilter: "all",
    searchTerm,
    page: 1,
    pageSize: 1,
  });

  const { data: enabledData } = useBusinesses({
    enabledFilter: "enabled",
    searchTerm,
    page: 1,
    pageSize: 1,
  });

  const { data: disabledData } = useBusinesses({
    enabledFilter: "disabled",
    searchTerm,
    page: 1,
    pageSize: 1,
  });

  // Main data query for current tab + page
  const { data, isLoading, error, refetch } = useBusinesses({
    enabledFilter: filter,
    searchTerm,
    page: currentPage,
    pageSize: BUSINESS_ITEMS_PER_PAGE,
  });

  const businesses = data?.businesses || [];
  const pagination = data?.pagination || {};

  const tabCounts = {
    all: allData?.pagination?.total || 0,
    enabled: enabledData?.pagination?.total || 0,
    disabled: disabledData?.pagination?.total || 0,
  };

  const getFilterLabel = (filterKey, fallbackLabel) => {
    const labelByFilter = {
      enabled: t("tabs.active"),
      disabled: t("tabs.inactive"),
      all: t("tabs.all"),
    };
    return labelByFilter[filterKey] || fallbackLabel;
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
    updateUrlState("filter", newFilter);
    updateUrlState("currentPage", "1");
  };

  const handleSearchChange = (term) => {
    setSearchInput(term);
    updateUrlState("searchTerm", term);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    updateUrlState("searchTerm", "");
    updateUrlState("currentPage", "1");
  };

  const handleRefresh = () => {
    refetch();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlState("currentPage", page.toString());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleInfoClick = () => {
    setIsInfoModalOpen(true);
  };

  const handleCreateBusiness = () => {
    navigate("/menu/stores/create/address");
  };

  return (
    <div>
      <PageHeader
        title={t("header.title")}
        subtitle={t("header.subtitle")}
        selectedBrand={selectedBrand}
      />

      <div className="space-y-4 sm:space-y-6">
        {/* Action Buttons */}
        <div className="flex flex-col justify-end gap-3 sm:flex-row sm:items-center sm:gap-3">
          <Button
            onClick={handleCreateBusiness}
            variant="default"
            size="default"
            className="h-9 w-full bg-blue-600 text-white hover:bg-blue-700 sm:h-10 sm:w-auto"
          >
            <Icon name="Plus" className="mr-2 h-4 w-4" />
            <span className="text-sm sm:text-base">
              {t("actions.createBusiness")}
            </span>
          </Button>
          <button
            onClick={handleInfoClick}
            className="inline-flex h-9 w-full items-center justify-center rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-xs font-medium text-gray-300 hover:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none sm:h-10 sm:w-auto sm:text-sm"
          >
            <svg
              className="mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm sm:text-base">{t("actions.info")}</span>
          </button>
        </div>

        {/* Status Tabs */}
        <div className="border-b border-gray-800">
          <nav className="flex space-x-4 overflow-x-auto sm:space-x-8">
            {BUSINESS_FILTER_TABS.map(({ key, label, color }) => {
              const count = tabCounts[key] || 0;
              return (
                <button
                  key={key}
                  onClick={() => handleFilterChange(key)}
                  className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                    filter === key
                      ? `border-current ${color}`
                      : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"
                  }`}
                >
                  {getFilterLabel(key, label)} ({count})
                </button>
              );
            })}
          </nav>
        </div>

        {/* Search and Refresh */}
        <BusinessFilters
          searchTerm={searchInput}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
          onRefresh={handleRefresh}
          isLoading={isLoading}
        />

        {/* Business List */}
        <BusinessList
          businesses={businesses}
          pagination={pagination}
          isLoading={isLoading}
          error={error}
          onPageChange={handlePageChange}
          currentPage={currentPage}
          pageSize={BUSINESS_ITEMS_PER_PAGE}
        />
      </div>

      {/* Info Modal */}
      <BusinessInfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </div>
  );
};

export default StoresPage;
